import React, { useState, useEffect } from 'react';
import { fmtParede } from '../utils/tempoCv';
import {
  FiBookOpen,
  FiPlayCircle,
  FiTrendingUp,
  FiShield,
  FiDollarSign,
  FiTarget,
  FiAward,
  FiClock,
  FiChevronRight,
  FiChevronLeft,
  FiStar,
  FiPercent,
  FiPieChart,
  FiCheck,
  FiX,
  FiZap,
} from 'react-icons/fi';

// ─── Types ───

interface Quiz {
  pergunta: string;
  opcoes: string[];
  respostaCorreta: number;
  explicacao: string;
}

interface Lesson {
  titulo: string;
  conteudo: string;
  pontosPrincipais?: string[];
  dicaPratica?: string;
  quiz?: Quiz;
}

interface CourseModule {
  titulo: string;
  descricao: string;
  licoes: Lesson[];
}

interface Course {
  id: string;
  titulo: string;
  subtitulo: string;
  descricao: string;
  duracao: string;
  nivel: 'iniciante' | 'intermediario' | 'avancado';
  emoji: string;
  color: string;
  modulos: CourseModule[];
}

interface DicaDoDia {
  categoria: string;
  titulo: string;
  conteudo: string;
  icon: React.ReactNode;
  color: string;
}

interface Artigo {
  titulo: string;
  resumo: string;
  categoria: string;
  tempoLeitura: string;
  destaque?: boolean;
  conteudo: string;
  topicos?: string[];
  color: string;
}

// ─── Course Data ───

const courses: Course[] = [
  {
    id: 'money-boss',
    titulo: 'Torna-te o Boss do Teu Dinheiro',
    subtitulo: 'Financas pessoais sem complicacao',
    descricao: 'Descobre como controlar o teu dinheiro em vez de ele te controlar. Sem jargao, sem stress — so dicas reais para o dia-a-dia em Cabo Verde.',
    duracao: '30 min', nivel: 'iniciante', emoji: '\u{1F4B0}', color: '#4CAF50',
    modulos: [
      { titulo: 'O Dinheiro e Tu', descricao: 'Entender a tua relacao com o dinheiro', licoes: [
        { titulo: 'Porque o dinheiro "desaparece"?', conteudo: 'Ja alguma vez recebeste o salario e, uma semana depois, nao sabias para onde foi o dinheiro? Nao es o unico.\n\nO primeiro passo para controlar as tuas financas e entender para onde vai o teu dinheiro. Nao se trata de cortar tudo — trata-se de escolher conscientemente onde gastar.', pontosPrincipais: ['O dinheiro nao desaparece — vai para sitios que nao rastreamos', 'Pequenos gastos diarios acumulam rapido', 'Saber para onde vai = poder de escolha'], dicaPratica: 'Durante 1 semana, anota TUDO o que gastas no teu telemovel.', quiz: { pergunta: 'Qual e o primeiro passo para controlar as financas?', opcoes: ['Cortar todos os gastos', 'Saber para onde vai o dinheiro', 'Ganhar mais dinheiro', 'Pedir emprestado menos'], respostaCorreta: 1, explicacao: 'Antes de mudar habitos, precisas de saber para onde vai o teu dinheiro.' } },
        { titulo: 'A Regra 50/30/20 versao Cabo Verde', conteudo: 'Uma das formas mais simples de organizar o dinheiro:\n\n50% — Necessidades (renda, comida, transporte)\n30% — Desejos (saidas, roupa, lazer)\n20% — Futuro (poupanca, investimento)\n\nEm Cabo Verde, adapta as percentagens a tua realidade.', pontosPrincipais: ['50% para necessidades basicas', '30% para desejos e qualidade de vida', '20% para poupanca e futuro'], dicaPratica: 'Quando receberes o proximo salario, separa logo os 20% para poupanca.', quiz: { pergunta: 'Na regra 50/30/20, os 20% destinam-se a:', opcoes: ['Compras no supermercado', 'Saidas com amigos', 'Poupanca e investimento', 'Pagamento de dividas'], respostaCorreta: 2, explicacao: 'Os 20% sao para o teu futuro: poupanca, investimento e fundo de emergencia.' } },
        { titulo: 'O Poder de Dizer "Agora Nao"', conteudo: 'Compras por impulso sao o maior inimigo das financas pessoais.\n\nA tecnica dos 48 horas: quando quiseres comprar algo nao essencial, espera 48 horas. Se depois ainda quiseres, e couber no orcamento, compra sem culpa.', pontosPrincipais: ['Compras por impulso drenam o orcamento', 'A regra das 48 horas ajuda a decidir melhor', 'Dizer "nao" agora e dizer "sim" ao futuro'], quiz: { pergunta: 'Qual e a tecnica sugerida para evitar compras por impulso?', opcoes: ['Nunca comprar nada', 'Esperar 48 horas', 'So comprar ao final do mes', 'Pedir opiniao a um amigo'], respostaCorreta: 1, explicacao: 'A tecnica das 48h da-te tempo para decidir se realmente queres ou precisas daquilo.' } },
      ] },
      { titulo: 'Poupar Sem Sofrer', descricao: 'Metodos praticos de poupanca', licoes: [
        { titulo: 'O Fundo de Emergencia', conteudo: 'O fundo de emergencia e o teu "airbag" financeiro. Serve para imprevistos.\n\nObjetivo: 3 a 6 meses das tuas despesas mensais.\n\nEm Cabo Verde, com despesas medias de 30.000$ a 50.000$ por mes, isso significa guardar entre 90.000$ e 300.000$.\n\nComeca com 5.000$ por mes. Em 2 anos tens um bom colchao de seguranca.', pontosPrincipais: ['O fundo de emergencia protege-te de imprevistos', 'Meta: 3 a 6 meses de despesas', 'Comeca pequeno — 5.000$/mes ja e um bom inicio'], dicaPratica: 'Abre uma conta poupanca separada e configura uma transferencia automatica mensal.' },
        { titulo: 'Desafio das 52 Semanas', conteudo: 'Semana 1: guarda 100$\nSemana 2: guarda 200$\nSemana 3: guarda 300$\n...\nSemana 52: guarda 5.200$\n\nNo final do ano teras guardado 137.800$!\n\nSe as ultimas semanas forem dificeis, faz ao contrario.', pontosPrincipais: ['Comeca com 100$ e aumenta 100$ por semana', 'Total ao final do ano: 137.800$', 'Pode ser feito ao contrario'], dicaPratica: 'Comeca hoje! Guarda 100$ numa conta ou envelope.', quiz: { pergunta: 'Quanto poupas no total com o desafio das 52 semanas?', opcoes: ['52.000$', '100.000$', '137.800$', '260.000$'], respostaCorreta: 2, explicacao: 'A soma de 100+200+300+...+5200 = 137.800$. Um belo pe-de-meia!' } },
      ] },
    ],
  },
  {
    id: 'budget-master',
    titulo: 'Orcamento Ninja',
    subtitulo: 'O mapa do teu dinheiro',
    descricao: 'Aprende a criar um orcamento que funciona de verdade. Sem folhas de Excel complicadas.',
    duracao: '20 min', nivel: 'iniciante', emoji: '\u{1F3AF}', color: '#2196F3',
    modulos: [
      { titulo: 'Criar o Teu Orcamento', descricao: 'Passo a passo', licoes: [
        { titulo: 'Quanto entra, quanto sai?', conteudo: 'O orcamento comeca com duas perguntas simples:\n\n1. Quanto ganhas por mes?\n2. Quanto gastas por mes?\n\nFixos: renda, agua, luz, telefone\nVariaveis: comida, saidas, roupa, lazer\n\nSe os gastos sao maiores que os ganhos, tens um problema.', pontosPrincipais: ['Lista todas as fontes de rendimento', 'Separa gastos fixos de variaveis', 'Ganhos devem ser sempre maiores que gastos'], quiz: { pergunta: 'Qual e a primeira coisa a fazer ao criar um orcamento?', opcoes: ['Cortar gastos', 'Listar receitas e despesas', 'Abrir conta poupanca', 'Investir'], respostaCorreta: 1, explicacao: 'Saber quanto entra e quanto sai e a base de qualquer orcamento.' } },
        { titulo: 'O Metodo dos Envelopes Digital', conteudo: 'Separas o dinheiro em categorias no inicio do mes. Quando o envelope acaba, acabou.\n\nVersao digital com Pagali:\n- Cria categorias: Comida, Transporte, Lazer, Poupanca\n- Atribui um valor a cada uma\n- Usa a Wallet Pagali para acompanhar', pontosPrincipais: ['Divide o dinheiro em categorias', 'Quando o "envelope" acaba, nao gastas mais', 'A Wallet Pagali ajuda a rastrear'], dicaPratica: 'Este mes, experimenta separar o dinheiro em 4 categorias.' },
        { titulo: 'Reduzir sem perder qualidade de vida', conteudo: 'Poupar nao significa viver mal:\n\n- Cozinha em casa 3x por semana\n- Usa hiaces/autocarros quando possivel\n- Compara precos entre lojas\n- Cancela subscricoes que nao usas\n- Partilha servicos com familia/amigos', pontosPrincipais: ['Pequenas mudancas = grandes poupancas', 'Cozinhar em casa pode poupar 15.000$ por mes', 'Comparar precos e um habito de pessoas ricas'], quiz: { pergunta: 'Qual destas e uma forma inteligente de reduzir gastos?', opcoes: ['Nao comer', 'Comparar precos entre lojas', 'Nunca sair com amigos', 'Nao usar transporte'], respostaCorreta: 1, explicacao: 'Comparar precos e simples, nao afeta qualidade de vida.' } },
      ] },
    ],
  },
  {
    id: 'digital-shield',
    titulo: 'Escudo Digital',
    subtitulo: 'Protege o teu dinheiro online',
    descricao: 'Golpes, phishing, passwords fracas... Aprende a navegar o mundo digital sem armadilhas.',
    duracao: '25 min', nivel: 'iniciante', emoji: '\u{1F6E1}\u{FE0F}', color: '#E91E63',
    modulos: [
      { titulo: 'Ameacas Comuns', descricao: 'Conhecer para se proteger', licoes: [
        { titulo: 'Phishing: o golpe mais comum', conteudo: 'Phishing e quando alguem se faz passar por uma empresa legitima para roubar dados.\n\nExemplos em Cabo Verde:\n- SMS: "O seu cartao Vinti4 foi bloqueado. Clique aqui..."\n- Email falso do banco a pedir password\n- WhatsApp com link para "premio"\n\nRegra de ouro: NENHUM banco pede password por SMS, email ou WhatsApp.', pontosPrincipais: ['Phishing imita empresas reais', 'Nunca clicar em links suspeitos', 'Bancos NUNCA pedem password por mensagem'], quiz: { pergunta: 'Recebes SMS: "Vinti4: Cartao bloqueado, clique para desbloquear". O que fazes?', opcoes: ['Clico no link', 'Ignoro e ligo para a SISP', 'Respondo ao SMS', 'Reencaminho para amigos'], respostaCorreta: 1, explicacao: 'Nunca cliques em links de SMS. Liga diretamente para a SISP.' } },
        { titulo: 'Passwords que protegem a serio', conteudo: 'Uma boa password:\n- Minimo 12 caracteres\n- Mistura de maiusculas, minusculas, numeros e simbolos\n- Nao usa datas de nascimento\n- E diferente para cada conta\n\nTruque: pensa numa frase e usa as iniciais. Ex: "Eu adoro batuku de Santiago!" -> "EabdS!"', pontosPrincipais: ['Passwords longas sao mais seguras', 'Cada conta deve ter password diferente', 'Usa frases como base'], dicaPratica: 'Muda agora a password da tua conta principal.' },
        { titulo: 'Compras online seguras', conteudo: 'Antes de comprar online:\n\n1. Verifica o cadeado (HTTPS)\n2. Pesquisa a reputacao da loja\n3. Desconfia de precos muito bons\n4. Usa cartao virtual ou com limite\n5. Guarda o comprovativo', pontosPrincipais: ['HTTPS obrigatorio para pagamentos', 'Preco muito baixo = possivel fraude', 'Usar cartao com limite para compras online'], quiz: { pergunta: 'Qual sinal indica que um site e seguro para pagamento?', opcoes: ['Tem muitas cores', 'Tem cadeado (HTTPS)', 'Tem fotos bonitas', 'Foi partilhado no Facebook'], respostaCorreta: 1, explicacao: 'O cadeado HTTPS garante comunicacao encriptada.' } },
      ] },
    ],
  },
  {
    id: 'invest-start',
    titulo: 'O Teu Dinheiro a Trabalhar',
    subtitulo: 'Investir nao e so para ricos',
    descricao: 'Desmistifica o mundo dos investimentos. Mesmo com pouco, podes fazer o teu dinheiro crescer.',
    duracao: '45 min', nivel: 'intermediario', emoji: '\u{1F4C8}', color: '#9C27B0',
    modulos: [
      { titulo: 'Fundamentos do Investimento', descricao: 'O basico que precisas', licoes: [
        { titulo: 'Poupar vs Investir', conteudo: 'Poupar e guardar dinheiro. Investir e colocar dinheiro a trabalhar.\n\nPoupanca: risco zero, retorno baixo\nInvestimento: risco variavel, retorno potencialmente maior\n\nA inflacao corroi a poupanca parada. Com 3% de inflacao, 100.000$ hoje valem 74.000$ daqui a 10 anos.', pontosPrincipais: ['Poupanca = guardar com seguranca', 'Investimento = fazer o dinheiro crescer', 'Inflacao diminui o valor da poupanca parada'], quiz: { pergunta: 'Porque e que so poupar pode nao ser suficiente?', opcoes: ['Porque os bancos roubam', 'Porque a inflacao reduz o valor', 'Porque e ilegal', 'Porque os juros sao altos'], respostaCorreta: 1, explicacao: 'A inflacao faz com que o mesmo dinheiro compre menos ao longo do tempo.' } },
        { titulo: 'Tipos de investimento em CV', conteudo: '1. Deposito a Prazo — Risco baixo, 2-4%/ano\n2. Obrigacoes do Tesouro — Risco baixo, 3-5%/ano\n3. Bolsa de Valores de CV — Risco medio\n4. Imobiliario — Risco medio\n5. Negocio proprio — Risco alto', pontosPrincipais: ['Cabo Verde tem bolsa de valores (BVC)', 'Obrigacoes do Tesouro sao das opcoes mais seguras', 'Diversificar e a chave'], dicaPratica: 'Vai ao teu banco e pergunta sobre Obrigacoes do Tesouro.' },
        { titulo: 'O poder dos juros compostos', conteudo: 'Se investires 10.000$ por mes com retorno de 5% ao ano:\n\nApos 5 anos: ~680.000$\nApos 10 anos: ~1.550.000$\nApos 20 anos: ~4.100.000$\n\nO segredo? Tempo. Quanto mais cedo comecares, melhor.', pontosPrincipais: ['Juros compostos = juros sobre juros', 'O tempo e o teu maior aliado', 'Consistencia importa mais que o montante'], quiz: { pergunta: 'Se investires 10.000$/mes a 5%/ano, quanto teras apos 20 anos?', opcoes: ['2.400.000$', '3.000.000$', '4.100.000$', '1.200.000$'], respostaCorreta: 2, explicacao: 'Gracias aos juros compostos, 2.400.000$ investidos viram ~4.100.000$!' } },
      ] },
    ],
  },
  {
    id: 'digital-pay',
    titulo: 'Paga Sem Filas, Vive Sem Stress',
    subtitulo: 'Guia dos pagamentos digitais',
    descricao: 'Chega de filas. Aprende a pagar tudo pelo telemovel — luz, agua, recargas e mais.',
    duracao: '15 min', nivel: 'iniciante', emoji: '\u{26A1}', color: '#FF9800',
    modulos: [
      { titulo: 'Pagamentos Digitais em CV', descricao: 'Tudo o que precisas', licoes: [
        { titulo: 'O que podes pagar pelo telemovel?', conteudo: 'Com o Pagali e a rede Vinti4:\n\n- Luz (Electra SA, EDEC)\n- Agua (Agua Brava, ADSN)\n- Recargas telefonicas (Alou, Unitel T+)\n- Eventos e bilhetes\n- Compras em lojas online\n- Donativos\n- Servicos do governo', pontosPrincipais: ['Pagamentos de servicos basicos', 'Compras online e em lojas', 'Tudo protegido pela rede Vinti4'], quiz: { pergunta: 'Qual destes servicos NAO podes pagar pelo Pagali?', opcoes: ['Luz da Electra', 'Recarga Alou', 'Bilhetes de aviao da TACV', 'Agua da ADSN'], respostaCorreta: 2, explicacao: 'Bilhetes de aviao ainda nao estao disponiveis, mas quase tudo o resto ja podes pagar!' } },
        { titulo: 'Cartao Vinti4 vs Wallet Digital', conteudo: 'Cartao Vinti4: cartao fisico, aceite em lojas e online\nWallet Pagali: carteira digital no telemovel, pagamentos rapidos\n\nO ideal? Ter ambos. O cartao como backup, a wallet para o dia-a-dia.', pontosPrincipais: ['Cartao Vinti4 e o standard em Cabo Verde', 'Wallet digital e mais pratica', 'Ambos sao seguros com verificacao 3DS'], dicaPratica: 'Se ainda nao tens a Wallet configurada no Pagali, faz isso agora!' },
      ] },
    ],
  },
];

const dicasDoDia: DicaDoDia[] = [
  { titulo: 'Regra 50/30/20', conteudo: 'Divide o teu salario: 50% para necessidades, 30% para desejos e 20% para poupanca.', categoria: 'Poupanca', icon: <FiDollarSign />, color: 'text-green-500' },
  { titulo: 'Protege as tuas passwords', conteudo: 'Usa passwords unicas para cada conta e ativa autenticacao de dois fatores.', categoria: 'Seguranca', icon: <FiShield />, color: 'text-blue-500' },
  { titulo: 'Comeca pequeno', conteudo: 'Nao precisas de muito dinheiro para comecar a investir. O importante e comecar.', categoria: 'Investimento', icon: <FiTrendingUp />, color: 'text-purple-500' },
  { titulo: 'Pagamentos seguros', conteudo: 'Verifica sempre o URL do site antes de inserir dados de pagamento.', categoria: 'Digital', icon: <FiShield />, color: 'text-amber-500' },
  { titulo: 'Fundo de emergencia', conteudo: 'Guarda pelo menos 3 a 6 meses de despesas num fundo de emergencia.', categoria: 'Poupanca', icon: <FiTarget />, color: 'text-red-500' },
];

const artigos: Artigo[] = [
  { titulo: 'Como poupar dinheiro em Cabo Verde', resumo: 'Estrategias praticas adaptadas a realidade cabo-verdiana.', categoria: 'Poupanca', tempoLeitura: '5 min', destaque: true, color: '#4CAF50',
    topicos: ['Habitos diarios', 'Compras inteligentes', 'Servicos essenciais', 'Poupanca automatica'],
    conteudo: 'Poupar dinheiro em Cabo Verde tem os seus desafios proprios: o custo da insularidade, precos de importacao elevados e um mercado pequeno. Mas com estrategias certas, qualquer pessoa pode comecar a guardar dinheiro.\n\nHabitos diarios que fazem diferenca\n\nOs pequenos gastos sao os que mais nos escapam. Um cafe diario de 150$ sao 4.500$ por mes — 54.000$ por ano. Nao se trata de eliminar o cafe, mas de ter consciencia.\n\nAnota durante uma semana todos os gastos, por mais pequenos que sejam. Vais descobrir "fugas" que nem sabias que existiam.\n\nCompras inteligentes\n\nEm Cabo Verde, os precos variam bastante entre lojas. Dicas praticas:\n- Faz uma lista antes de ir ao supermercado e segue-a\n- Compara precos entre pelo menos 2 lojas\n- Compra a granel quando possivel (arroz, feijao, acucar)\n- Aproveita promocoes mas so compra o que realmente precisas\n- Nos mercados municipais encontras fruta, legumes e peixe mais baratos\n\nServicos essenciais\n\nA agua e a luz sao despesas fixas mas podes optimiza-las:\n- Usa lampadas LED (gastam 80% menos energia)\n- Desliga aparelhos da tomada quando nao usas\n- Paga as contas a tempo para evitar multas (usa o Pagali)\n\nPoupanca automatica\n\nNo dia em que recebes o salario:\n1. Separa logo 10-20% para poupanca\n2. Paga as contas fixas\n3. O que sobra e para viver\n\nSe ganhas 50.000$ e guardas 10.000$ por mes, em 1 ano tens 120.000$. Em 5 anos, 600.000$. A regra e simples: paga-te a ti primeiro.' },
  { titulo: 'Entender os juros compostos', resumo: 'O poder dos juros compostos e como podem trabalhar a teu favor.', categoria: 'Investimento', tempoLeitura: '7 min', color: '#9C27B0',
    topicos: ['O que sao', 'Juros simples vs compostos', 'Exemplos reais', 'Como comecar'],
    conteudo: 'Albert Einstein tera dito que os juros compostos sao "a oitava maravilha do mundo."\n\nO que sao juros compostos?\n\nJuros compostos sao juros que incidem sobre juros anteriores. O teu dinheiro cresce de forma exponencial.\n\nImagina que depositas 100.000$ a uma taxa de 5% ao ano:\n- Ano 1: ganhas 5.000$ → total 105.000$\n- Ano 2: ganhas 5.250$ (5% de 105.000$) → total 110.250$\n- Ano 3: ganhas 5.512$ → total 115.762$\n\nJuros simples vs compostos\n\nCom juros simples, os 100.000$ a 5% dariam sempre 5.000$/ano:\n- Em 10 anos: 150.000$ (simples) vs 162.889$ (compostos)\n- Em 20 anos: 200.000$ vs 265.330$\n- Em 30 anos: 250.000$ vs 432.194$\n\nExemplos reais em Cabo Verde\n\nDeposito a prazo a 3% ao ano:\n- 10.000$/mes durante 10 anos = 1.200.000$ investidos\n- Com juros compostos: ~1.397.000$ (ganho de 197.000$)\n\nObrigacoes do Tesouro a 5% ao ano:\n- 10.000$/mes durante 10 anos = ~1.553.000$ (ganho de 353.000$)\n\nComo comecar\n\n1. Abre uma conta poupanca com juros\n2. Configura transferencia automatica mensal\n3. Nao toques no dinheiro — deixa os juros acumularem\n4. Pergunta sobre depositos a prazo ou Obrigacoes do Tesouro\n\nA melhor altura para comecar era ha 10 anos. A segunda melhor e agora.' },
  { titulo: 'Evitar fraudes online', resumo: 'Sinais de alerta e como proteger o teu dinheiro na internet.', categoria: 'Seguranca', tempoLeitura: '4 min', destaque: true, color: '#2196F3',
    topicos: ['Tipos de fraude', 'Sinais de alerta', 'Proteccao', 'O que fazer se fores vitima'],
    conteudo: 'As fraudes online estao a crescer em Cabo Verde a medida que mais pessoas usam pagamentos digitais.\n\nTipos de fraude mais comuns\n\n1. Phishing por SMS — Mensagens a fingir que sao do banco ou da SISP, pedindo para clicar num link.\n\n2. Golpes no WhatsApp — Alguem faz-se passar por familiar e pede transferencia urgente.\n\n3. Lojas online falsas — Sites com precos irresistiveis. Pagam e nunca recebem o produto.\n\n4. Esquemas de investimento — Prometem retornos de 10-50% ao mes. Se parece bom demais, e golpe.\n\nSinais de alerta\n\n- Urgencia: "Tens de agir AGORA"\n- Pedido de dados sensiveis por mensagem\n- Links com URLs estranhos\n- Erros de portugues no texto\n- Promessas de ganhos garantidos elevados\n\nComo te proteger\n\n- Nunca partilhes PIN, password ou CVV por mensagem\n- Verifica sempre o URL (procura o cadeado HTTPS)\n- Liga directamente para o banco se receberes mensagem suspeita\n- Usa passwords diferentes para cada conta\n- Activa a autenticacao de dois factores\n\nO que fazer se fores vitima\n\n1. Contacta imediatamente o teu banco para bloquear o cartao\n2. Muda todas as passwords comprometidas\n3. Guarda provas (screenshots)\n4. Faz queixa na Policia Nacional\n5. Reporta a SISP se envolver cartoes Vinti4' },
  { titulo: 'Pagamentos digitais em Cabo Verde', resumo: 'Guia completo sobre pagamentos digitais em Cabo Verde.', categoria: 'Digital', tempoLeitura: '6 min', color: '#FF9800',
    topicos: ['Rede Vinti4', 'Pagamentos pelo telemovel', 'Carteiras digitais', 'Futuro dos pagamentos'],
    conteudo: 'Cabo Verde tem vindo a transformar-se digitalmente, e os pagamentos nao sao excepcao.\n\nA rede Vinti4\n\nA SISP opera a rede Vinti4, que conecta todos os bancos de Cabo Verde. Com um cartao Vinti4 podes:\n- Levantar dinheiro em qualquer Caixa Automatico\n- Pagar em lojas com POS\n- Fazer compras online\n- Pagar servicos (luz, agua, telefone)\n- Fazer recargas telefonicas\n\nPagamentos pelo telemovel\n\nCom o Pagali podes:\n- Pagar a conta da Electra SA ou EDEC\n- Carregar o contador de agua (Agua Brava, ADSN)\n- Recarregar o telemovel (Alou, Unitel T+)\n- Comprar bilhetes para eventos\n- Fazer donativos\n\nTudo em menos de 2 minutos.\n\nCarteiras digitais\n\nA Wallet Pagali permite guardar cartoes de forma segura no telemovel:\n- Nao precisas de andar com o cartao fisico\n- Pagamentos mais rapidos\n- Historico de transacoes sempre disponivel\n- Seguranca extra com verificacao 3DS\n\nO futuro dos pagamentos em CV\n\n- QR Code para pagamentos rapidos em lojas\n- Pagamentos contactless\n- Open Banking\n- Pagamentos instantaneos 24/7\n\nA adopcao digital e uma oportunidade para todos.' },
  { titulo: 'Planear a reforma desde cedo', resumo: 'Porque e importante comecar a pensar na reforma agora.', categoria: 'Planeamento', tempoLeitura: '8 min', color: '#E91E63',
    topicos: ['Porque planear cedo', 'INPS e pensoes', 'Complementar a reforma', 'Estrategias praticas'],
    conteudo: 'Se tens 25 anos, a reforma parece estar a uma eternidade. Mas a matematica e clara: quanto mais cedo comecares, menos esforco precisas.\n\nPorque planear cedo faz toda a diferenca\n\nMaria comeca aos 25 anos, guarda 5.000$/mes:\n- Aos 65 anos (40 anos): investiu 2.400.000$\n- Com juros compostos a 4%: tem ~5.900.000$\n\nJoao comeca aos 35 anos, guarda 10.000$/mes:\n- Aos 65 anos (30 anos): investiu 3.600.000$\n- Com juros compostos a 4%: tem ~6.900.000$\n\nJoao investiu 50% mais mas tem apenas 17% mais no final.\n\nINPS e pensoes em Cabo Verde\n\nO INPS e o sistema publico de pensoes:\n- Tu e o teu empregador contribuem mensalmente\n- Minimo 15 anos de contribuicoes para pensao completa\n- A pensao media do INPS pode nao cobrir todas as despesas\n\nComo complementar a reforma\n\n1. Depositos a prazo — Seguro e simples\n2. Obrigacoes do Tesouro — Retornos de 3-5%, muito seguro\n3. Imobiliario — Comprar para arrendar. Mercado em crescimento em Sal, Boa Vista e Santiago\n4. Poupanca programada — Bancos oferecem planos a longo prazo\n\nEstrategias por idade\n\nAos 20-30: comeca com 5-10% do salario, aceita mais risco\nAos 30-40: aumenta para 15-20%, diversifica\nAos 40-50: reduz risco, foco em obrigacoes\n\nO pior que podes fazer e nao fazer nada. Mesmo 2.000$ por mes e melhor que zero.' },
];

// ─── Progress helpers ───

const getProgress = (courseId: string): Set<string> => {
  try {
    const data = localStorage.getItem(`academy_${courseId}_completed`);
    return data ? new Set(JSON.parse(data)) : new Set();
  } catch { return new Set(); }
};

const saveProgress = (courseId: string, completed: Set<string>) => {
  localStorage.setItem(`academy_${courseId}_completed`, JSON.stringify([...completed]));
};

const getTotalLessons = (course: Course) =>
  course.modulos.reduce((sum, m) => sum + m.licoes.length, 0);

const getTotalCertificates = () =>
  courses.filter(c => getProgress(c.id).size >= getTotalLessons(c)).length;

// ─── Subcomponents ───

const getNivelColor = (nivel: string) => {
  switch (nivel) {
    case 'iniciante': return 'bg-green-100 text-green-700';
    case 'intermediario': return 'bg-amber-100 text-amber-700';
    case 'avancado': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getNivelLabel = (nivel: string) => {
  switch (nivel) {
    case 'intermediario': return 'Intermedio';
    case 'avancado': return 'Avancado';
    default: return 'Iniciante';
  }
};

const getCategoriaColor = (cat: string) => {
  switch (cat.toLowerCase()) {
    case 'poupanca': return 'bg-green-100 text-green-700';
    case 'investimento': return 'bg-purple-100 text-purple-700';
    case 'seguranca': return 'bg-blue-100 text-blue-700';
    case 'digital': return 'bg-amber-100 text-amber-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

// ─── Quiz Component ───

const QuizWidget: React.FC<{ quiz: Quiz; color: string }> = ({ quiz, color }) => {
  const [selected, setSelected] = useState<number | null>(null);
  const answered = selected !== null;
  const isCorrect = selected === quiz.respostaCorreta;

  return (
    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
      <div className="flex items-center gap-2 mb-3">
        <FiZap className="text-amber-500" />
        <span className="font-semibold text-gray-900">Quiz</span>
      </div>
      <p className="font-medium text-gray-900 mb-4">{quiz.pergunta}</p>
      <div className="space-y-2">
        {quiz.opcoes.map((opt, i) => {
          let cls = 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer';
          if (answered && i === quiz.respostaCorreta) cls = 'border-green-500 bg-green-50';
          else if (answered && i === selected) cls = 'border-red-500 bg-red-50';
          else if (!answered && i === selected) cls = `border-[${color}] bg-gray-50`;
          return (
            <button key={i} disabled={answered} onClick={() => setSelected(i)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${cls}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm">{opt}</span>
                {answered && i === quiz.respostaCorreta && <FiCheck className="text-green-600" />}
                {answered && i === selected && i !== quiz.respostaCorreta && <FiX className="text-red-600" />}
              </div>
            </button>
          );
        })}
      </div>
      {answered && (
        <div className={`mt-4 p-3 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <p className={`text-sm font-semibold ${isCorrect ? 'text-green-700' : 'text-amber-700'}`}>
            {isCorrect ? 'Correto!' : 'Quase!'}
          </p>
          <p className="text-sm text-gray-600 mt-1">{quiz.explicacao}</p>
        </div>
      )}
    </div>
  );
};

// ─── Lesson View ───

const LessonView: React.FC<{
  lesson: Lesson;
  course: Course;
  lessonKey: string;
  onComplete: () => void;
  onBack: () => void;
}> = ({ lesson, course, onComplete, onBack }) => (
  <div className="max-w-3xl mx-auto">
    <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4">
      <FiChevronLeft /> Voltar ao curso
    </button>

    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-100" style={{ borderLeftColor: course.color, borderLeftWidth: 4 }}>
        <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: course.color }}>{course.titulo}</p>
        <h2 className="text-xl font-bold text-gray-900">{lesson.titulo}</h2>
      </div>

      <div className="p-6 space-y-5">
        {lesson.conteudo.split('\n\n').map((p, i) => (
          <p key={i} className="text-gray-700 leading-relaxed whitespace-pre-line">{p}</p>
        ))}

        {lesson.pontosPrincipais && (
          <div className="rounded-xl p-4 border" style={{ backgroundColor: `${course.color}10`, borderColor: `${course.color}30` }}>
            <p className="text-sm font-semibold mb-3" style={{ color: course.color }}>Pontos-chave</p>
            <ul className="space-y-2">
              {lesson.pontosPrincipais.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <FiCheck className="mt-0.5 flex-shrink-0" style={{ color: course.color }} /> {p}
                </li>
              ))}
            </ul>
          </div>
        )}

        {lesson.dicaPratica && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-amber-700 mb-1">Dica Pratica</p>
            <p className="text-sm text-gray-700">{lesson.dicaPratica}</p>
          </div>
        )}

        {lesson.quiz && <QuizWidget quiz={lesson.quiz} color={course.color} />}
      </div>

      <div className="p-6 border-t border-gray-100">
        <button onClick={onComplete}
          className="w-full py-3 rounded-xl text-white font-semibold transition-colors"
          style={{ backgroundColor: course.color }}>
          Concluir Licao
        </button>
      </div>
    </div>
  </div>
);

// ─── Course Detail View ───

const CourseDetailView: React.FC<{
  course: Course;
  onBack: () => void;
  onOpenLesson: (moduleIdx: number, lessonIdx: number) => void;
  completed: Set<string>;
}> = ({ course, onBack, onOpenLesson, completed }) => {
  const total = getTotalLessons(course);
  const percent = total > 0 ? (completed.size / total) * 100 : 0;
  const isDone = completed.size >= total;

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4">
        <FiChevronLeft /> Voltar a Academia
      </button>

      {/* Header */}
      <div className="rounded-2xl p-6 text-white mb-6" style={{ background: `linear-gradient(135deg, ${course.color}, ${course.color}cc)` }}>
        <span className="text-3xl">{course.emoji}</span>
        <h1 className="text-2xl font-bold mt-2">{course.titulo}</h1>
        <p className="text-white/70 mt-1">{course.subtitulo}</p>
        <div className="flex gap-3 mt-4 text-sm text-white/80">
          <span className="flex items-center gap-1"><FiClock /> {course.duracao}</span>
          <span>{total} licoes</span>
          <span className="capitalize">{getNivelLabel(course.nivel)}</span>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium text-gray-700">Progresso</span>
          <span className="font-semibold" style={{ color: course.color }}>{completed.size}/{total} licoes</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${percent}%`, backgroundColor: course.color }} />
        </div>
        {isDone && (
          <div className="flex items-center gap-1 mt-2 text-sm font-semibold" style={{ color: course.color }}>
            <FiAward /> Curso completo!
          </div>
        )}
      </div>

      <p className="text-gray-600 mb-6">{course.descricao}</p>

      {/* Modules */}
      {course.modulos.map((mod, mi) => (
        <div key={mi} className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: course.color }}>
              {mi + 1}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{mod.titulo}</h3>
              <p className="text-xs text-gray-500">{mod.descricao}</p>
            </div>
          </div>
          <div className="ml-3 space-y-2">
            {mod.licoes.map((lesson, li) => {
              const key = `${mi}_${li}`;
              const done = completed.has(key);
              return (
                <button key={li} onClick={() => onOpenLesson(mi, li)}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    done ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-white hover:border-gray-200'
                  }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    done ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {done ? <FiCheck size={14} /> : <FiPlayCircle size={14} />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${done ? 'text-green-700' : 'text-gray-900'}`}>{lesson.titulo}</p>
                    {lesson.quiz && <p className="text-xs text-gray-400 flex items-center gap-1"><FiZap size={10} /> Com quiz</p>}
                  </div>
                  <FiChevronRight className="text-gray-400" />
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Certificate */}
      {isDone && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white text-center">
          <FiAward size={40} className="mx-auto mb-3" />
          <h3 className="text-xl font-bold">Parabens!</h3>
          <p className="text-white/80 mt-1">Completaste "{course.titulo}"</p>
        </div>
      )}
    </div>
  );
};

// ─── Article View ───

const ArticleView: React.FC<{ article: Artigo; onBack: () => void }> = ({ article, onBack }) => (
  <div className="max-w-3xl mx-auto">
    <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4">
      <FiChevronLeft /> Voltar a Academia
    </button>

    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6" style={{ borderLeftColor: article.color, borderLeftWidth: 4 }}>
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoriaColor(article.categoria)}`}>{article.categoria}</span>
          <span className="text-xs text-gray-400 flex items-center gap-1"><FiClock size={12} /> {article.tempoLeitura}</span>
          {article.destaque && <FiStar className="text-amber-500" size={14} />}
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{article.titulo}</h1>
        <p className="text-gray-500 mt-1">{article.resumo}</p>
      </div>

      {article.topicos && (
        <div className="px-6 pb-4 flex flex-wrap gap-2">
          {article.topicos.map((t, i) => (
            <span key={i} className="text-xs px-3 py-1 rounded-full border" style={{ borderColor: `${article.color}40`, color: article.color, backgroundColor: `${article.color}10` }}>{t}</span>
          ))}
        </div>
      )}

      <div className="px-6 pb-8">
        {article.conteudo.split('\n\n').map((p, i) => {
          if (p.length < 60 && !p.endsWith('.') && !p.includes('\n')) {
            return <h3 key={i} className="text-lg font-bold text-gray-900 mt-6 mb-2">{p}</h3>;
          }
          if (p.includes('\n- ')) {
            return (
              <div key={i} className="mb-3">
                {p.split('\n').map((line, j) => {
                  if (line.startsWith('- ')) return <div key={j} className="flex items-start gap-2 ml-2 mb-1"><FiCheck className="mt-1 flex-shrink-0" style={{ color: article.color }} size={14} /><span className="text-sm text-gray-700">{line.substring(2)}</span></div>;
                  return <p key={j} className="text-gray-700 leading-relaxed">{line}</p>;
                })}
              </div>
            );
          }
          return <p key={i} className="text-gray-700 leading-relaxed mb-3 whitespace-pre-line">{p}</p>;
        })}
      </div>
    </div>
  </div>
);

// ─── Main Page ───

// ─── Budget Calculator ───
const BudgetCalculator: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [rendimento, setRendimento] = useState('');
  const [despesas, setDespesas] = useState([
    { nome: 'Renda/Habitação', valor: '' },
    { nome: 'Alimentação', valor: '' },
    { nome: 'Transporte', valor: '' },
    { nome: 'Energia/Água', valor: '' },
    { nome: 'Telecomunicações', valor: '' },
    { nome: 'Educação', valor: '' },
    { nome: 'Saúde', valor: '' },
    { nome: 'Lazer', valor: '' },
    { nome: 'Outros', valor: '' },
  ]);

  const totalDespesas = despesas.reduce((s, d) => s + (parseFloat(d.valor) || 0), 0);
  const rend = parseFloat(rendimento) || 0;
  const saldo = rend - totalDespesas;
  const percPoupanca = rend > 0 ? ((saldo / rend) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <FiPieChart className="text-green-600" /> Calculadora de Orçamento
        </h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><FiX /></button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Rendimento Mensal (CVE)</label>
        <input type="number" value={rendimento} onChange={e => setRendimento(e.target.value)}
          placeholder="Ex: 80000" className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500" />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Despesas Mensais</h3>
        <div className="space-y-2">
          {despesas.map((d, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-gray-600 w-40 flex-shrink-0">{d.nome}</span>
              <input type="number" value={d.valor} placeholder="0"
                onChange={e => { const n = [...despesas]; n[i] = { ...d, valor: e.target.value }; setDespesas(n); }}
                className="flex-1 p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500" />
            </div>
          ))}
        </div>
      </div>

      {rend > 0 && (
        <div className={`p-4 rounded-xl border ${saldo >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500">Rendimento</p>
              <p className="font-bold text-gray-900">{rend.toLocaleString('pt-CV')}$</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Despesas</p>
              <p className="font-bold text-red-600">{totalDespesas.toLocaleString('pt-CV')}$</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Saldo</p>
              <p className={`font-bold ${saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>{saldo.toLocaleString('pt-CV')}$</p>
            </div>
          </div>
          {rend > 0 && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Despesas vs Rendimento</span>
                <span>{Math.min(100, (totalDespesas / rend * 100)).toFixed(0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className={`h-2 rounded-full ${totalDespesas / rend > 1 ? 'bg-red-500' : totalDespesas / rend > 0.8 ? 'bg-yellow-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min(100, totalDespesas / rend * 100)}%` }} />
              </div>
              <p className="text-xs mt-2 text-gray-600">
                {percPoupanca >= 20 ? '✅ Excelente! Estás a poupar mais de 20% do rendimento.' :
                 percPoupanca >= 10 ? '👍 Bom! Tenta aumentar a poupança para 20%.' :
                 percPoupanca >= 0 ? '⚠️ Atenção: tenta reduzir despesas para poupar pelo menos 10%.' :
                 '🚨 As despesas excedem o rendimento. Revê o orçamento urgentemente.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Savings Simulator ───
const SavingsSimulator: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [poupancaMensal, setPoupancaMensal] = useState('');
  const [meses, setMeses] = useState('12');
  const [taxaAnual, setTaxaAnual] = useState('3');

  const pm = parseFloat(poupancaMensal) || 0;
  const n = parseInt(meses) || 0;
  const r = (parseFloat(taxaAnual) || 0) / 100 / 12;

  const totalSemJuros = pm * n;
  const totalComJuros = r > 0
    ? pm * ((Math.pow(1 + r, n) - 1) / r)
    : totalSemJuros;
  const jurosGanhos = totalComJuros - totalSemJuros;

  const marcos = [6, 12, 24, 36, 60].filter(m => m <= n || n === 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <FiTrendingUp className="text-purple-600" /> Simulador de Poupança
        </h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><FiX /></button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Poupança Mensal (CVE)</label>
          <input type="number" value={poupancaMensal} onChange={e => setPoupancaMensal(e.target.value)}
            placeholder="Ex: 5000" className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Período (meses)</label>
          <input type="number" value={meses} onChange={e => setMeses(e.target.value)}
            placeholder="12" className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Taxa Anual (%)</label>
          <input type="number" value={taxaAnual} onChange={e => setTaxaAnual(e.target.value)}
            placeholder="3" step="0.1" className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
        </div>
      </div>

      {pm > 0 && n > 0 && (
        <>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500">Total Depositado</p>
                <p className="font-bold text-gray-900">{totalSemJuros.toLocaleString('pt-CV', { maximumFractionDigits: 0 })}$</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Juros Ganhos</p>
                <p className="font-bold text-purple-600">+{jurosGanhos.toLocaleString('pt-CV', { maximumFractionDigits: 0 })}$</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Final</p>
                <p className="font-bold text-green-600">{totalComJuros.toLocaleString('pt-CV', { maximumFractionDigits: 0 })}$</p>
              </div>
            </div>
          </div>

          {marcos.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Evolução da Poupança</h3>
              <div className="space-y-2">
                {marcos.map(m => {
                  const total = r > 0 ? pm * ((Math.pow(1 + r, m) - 1) / r) : pm * m;
                  return (
                    <div key={m} className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-20 flex-shrink-0">{m} meses</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full transition-all"
                          style={{ width: `${(total / totalComJuros) * 100}%` }} />
                      </div>
                      <span className="text-sm font-medium text-gray-800 w-24 text-right">{total.toLocaleString('pt-CV', { maximumFractionDigits: 0 })}$</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Financial Goals ───
interface Goal {
  id: string;
  nome: string;
  valor: number;
  poupado: number;
  prazo: string;
}

const FinancialGoals: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const STORAGE_KEY = 'pagali_financial_goals';
  const [goals, setGoals] = useState<Goal[]>(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });
  const [showForm, setShowForm] = useState(false);
  const [nome, setNome] = useState('');
  const [valor, setValor] = useState('');
  const [prazo, setPrazo] = useState('');

  const saveGoals = (g: Goal[]) => { setGoals(g); localStorage.setItem(STORAGE_KEY, JSON.stringify(g)); };

  const addGoal = () => {
    if (!nome.trim() || !valor.trim()) return;
    const newGoal: Goal = { id: Date.now().toString(), nome: nome.trim(), valor: parseFloat(valor), poupado: 0, prazo };
    saveGoals([...goals, newGoal]);
    setNome(''); setValor(''); setPrazo(''); setShowForm(false);
  };

  const addProgress = (id: string) => {
    const input = prompt('Quanto poupaste para esta meta? (CVE)');
    const amount = parseFloat(input || '');
    if (!amount || amount <= 0) return;
    saveGoals(goals.map(g => g.id === id ? { ...g, poupado: Math.min(g.valor, g.poupado + amount) } : g));
  };

  const removeGoal = (id: string) => {
    saveGoals(goals.filter(g => g.id !== id));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <FiTarget className="text-blue-600" /> Metas Financeiras
        </h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><FiX /></button>
      </div>

      {goals.length === 0 && !showForm && (
        <div className="text-center py-8 bg-blue-50 rounded-xl border border-blue-100">
          <FiTarget className="text-4xl text-blue-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">Ainda não tens metas definidas. Cria a tua primeira meta!</p>
          <button onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors">
            + Nova Meta
          </button>
        </div>
      )}

      {showForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <input type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome da meta (ex: Viagem, Carro, Emergência)"
            className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" value={valor} onChange={e => setValor(e.target.value)} placeholder="Valor objetivo (CVE)"
              className="p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            <input type="date" value={prazo} onChange={e => setPrazo(e.target.value)}
              className="p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex gap-2">
            <button onClick={addGoal} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">Guardar</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">Cancelar</button>
          </div>
        </div>
      )}

      {goals.length > 0 && (
        <div className="space-y-3">
          {goals.map(g => {
            const perc = g.valor > 0 ? (g.poupado / g.valor) * 100 : 0;
            const isComplete = perc >= 100;
            return (
              <div key={g.id} className={`p-4 rounded-xl border ${isComplete ? 'bg-green-50 border-green-200' : 'bg-white border-gray-100'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {isComplete && <FiCheck className="text-green-600" />}
                    <h4 className="font-semibold text-gray-900">{g.nome}</h4>
                  </div>
                  <div className="flex items-center gap-1">
                    {!isComplete && (
                      <button onClick={() => addProgress(g.id)} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">+ Adicionar</button>
                    )}
                    <button onClick={() => removeGoal(g.id)} className="text-xs px-2 py-1 text-red-500 hover:bg-red-50 rounded-lg"><FiX /></button>
                  </div>
                </div>
                <div className="flex justify-between text-sm text-gray-500 mb-1">
                  <span>{g.poupado.toLocaleString('pt-CV')}$ / {g.valor.toLocaleString('pt-CV')}$</span>
                  <span>{perc.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className={`h-2.5 rounded-full transition-all ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(100, perc)}%` }} />
                </div>
                {g.prazo && (
                  <p className="text-xs text-gray-400 mt-2">Prazo: {fmtParede(g.prazo, { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                )}
              </div>
            );
          })}
          {!showForm && (
            <button onClick={() => setShowForm(true)}
              className="w-full p-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-blue-300 hover:text-blue-600 transition-colors">
              + Nova Meta
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Academia: React.FC = () => {
  const [view, setView] = useState<'home' | 'course' | 'lesson' | 'article' | 'budget' | 'savings' | 'goals'>('home');
  const [selectedArticle, setSelectedArticle] = useState<Artigo | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<{ mi: number; li: number } | null>(null);
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [dicaAtual, setDicaAtual] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setDicaAtual(p => (p + 1) % dicasDoDia.length), 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedCourse) setCompleted(getProgress(selectedCourse.id));
  }, [selectedCourse]);

  const openCourse = (c: Course) => { setSelectedCourse(c); setView('course'); };
  const openLesson = (mi: number, li: number) => { setSelectedLesson({ mi, li }); setView('lesson'); };

  const completeLesson = () => {
    if (!selectedCourse || !selectedLesson) return;
    const key = `${selectedLesson.mi}_${selectedLesson.li}`;
    const next = new Set(completed);
    next.add(key);
    setCompleted(next);
    saveProgress(selectedCourse.id, next);
    setView('course');
  };

  // ── Calculator Views ──
  if (view === 'budget') {
    return <div className="p-6"><BudgetCalculator onClose={() => setView('home')} /></div>;
  }
  if (view === 'savings') {
    return <div className="p-6"><SavingsSimulator onClose={() => setView('home')} /></div>;
  }
  if (view === 'goals') {
    return <div className="p-6"><FinancialGoals onClose={() => setView('home')} /></div>;
  }

  // ── Article View ──
  if (view === 'article' && selectedArticle) {
    return (
      <div className="p-6">
        <ArticleView article={selectedArticle} onBack={() => { setView('home'); setSelectedArticle(null); }} />
      </div>
    );
  }

  // ── Lesson View ──
  if (view === 'lesson' && selectedCourse && selectedLesson) {
    const lesson = selectedCourse.modulos[selectedLesson.mi].licoes[selectedLesson.li];
    const key = `${selectedLesson.mi}_${selectedLesson.li}`;
    return (
      <div className="p-6">
        <LessonView lesson={lesson} course={selectedCourse} lessonKey={key}
          onComplete={completeLesson} onBack={() => setView('course')} />
      </div>
    );
  }

  // ── Course Detail View ──
  if (view === 'course' && selectedCourse) {
    return (
      <div className="p-6">
        <CourseDetailView course={selectedCourse} completed={completed}
          onBack={() => { setView('home'); setSelectedCourse(null); }} onOpenLesson={openLesson} />
      </div>
    );
  }

  // ── Home View ──
  const dica = dicasDoDia[dicaAtual];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl">
            <FiBookOpen className="text-2xl text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Academia Pagali</h1>
            <p className="text-gray-500">Literacia financeira e digital para todos</p>
          </div>
        </div>
      </div>

      {/* Dica do Dia */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className={`p-3 bg-white rounded-xl shadow-sm text-xl ${dica.color}`}>{dica.icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-amber-600 uppercase tracking-wide">Dica do Dia</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoriaColor(dica.categoria)}`}>{dica.categoria}</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">{dica.titulo}</h3>
            <p className="text-gray-600">{dica.conteudo}</p>
          </div>
        </div>
        <div className="flex justify-center gap-2 mt-4">
          {dicasDoDia.map((_, i) => (
            <button key={i} onClick={() => setDicaAtual(i)}
              className={`h-2 rounded-full transition-all ${i === dicaAtual ? 'bg-amber-500 w-6' : 'bg-amber-300 w-2'}`} />
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { icon: <FiPlayCircle className="text-purple-600" />, bg: 'bg-purple-100', val: courses.length, label: 'Mini-cursos' },
          { icon: <FiBookOpen className="text-blue-600" />, bg: 'bg-blue-100', val: artigos.length, label: 'Artigos' },
          { icon: <FiPercent className="text-green-600" />, bg: 'bg-green-100', val: 3, label: 'Calculadoras' },
          { icon: <FiAward className="text-amber-600" />, bg: 'bg-amber-100', val: getTotalCertificates(), label: 'Certificados' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${s.bg} rounded-lg`}>{s.icon}</div>
              <div><p className="text-2xl font-bold text-gray-900">{s.val}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Courses */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <FiPlayCircle className="text-purple-500" /> Mini-Cursos
          </h2>
          <div className="space-y-3">
            {courses.map((c) => {
              const progress = getProgress(c.id);
              const total = getTotalLessons(c);
              const pct = total > 0 ? Math.round((progress.size / total) * 100) : 0;
              return (
                <button key={c.id} onClick={() => openCourse(c)}
                  className="w-full text-left bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{c.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-medium text-gray-900">{c.titulo}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${getNivelColor(c.nivel)}`}>
                          {getNivelLabel(c.nivel)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{c.descricao}</p>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span className="flex items-center gap-1"><FiClock /> {c.duracao}</span>
                        {pct > 0 && <span className="font-semibold" style={{ color: c.color }}>{pct}%</span>}
                      </div>
                      {pct > 0 && (
                        <div className="h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: c.color }} />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Articles */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <FiBookOpen className="text-blue-500" /> Artigos
          </h2>
          <div className="space-y-3">
            {artigos.map((a, i) => (
              <div key={i} onClick={() => { setSelectedArticle(a); setView('article'); }} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex items-start gap-3">
                  {a.destaque && <FiStar className="text-amber-500 mt-1 flex-shrink-0" />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoriaColor(a.categoria)}`}>{a.categoria}</span>
                      <span className="text-xs text-gray-400">{a.tempoLeitura}</span>
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1">{a.titulo}</h3>
                    <p className="text-sm text-gray-500">{a.resumo}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Calculators */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <FiPercent className="text-green-500" /> Ferramentas e Calculadoras
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: <FiPieChart className="text-xl text-green-600" />, title: 'Calculadora de Orçamento', desc: 'Planeia as tuas despesas mensais.', from: 'from-green-50', to: 'to-emerald-50', border: 'border-green-100', action: () => setView('budget') },
            { icon: <FiTrendingUp className="text-xl text-purple-600" />, title: 'Simulador de Poupança', desc: 'Quanto podes poupar ao longo do tempo.', from: 'from-purple-50', to: 'to-violet-50', border: 'border-purple-100', action: () => setView('savings') },
            { icon: <FiTarget className="text-xl text-blue-600" />, title: 'Metas Financeiras', desc: 'Define e acompanha os teus objetivos.', from: 'from-blue-50', to: 'to-cyan-50', border: 'border-blue-100', action: () => setView('goals') },
          ].map((t, i) => (
            <div key={i} onClick={t.action} className={`bg-gradient-to-br ${t.from} ${t.to} rounded-xl p-5 border ${t.border} hover:shadow-md transition-shadow cursor-pointer`}>
              <div className="p-3 bg-white rounded-xl shadow-sm w-fit mb-3">{t.icon}</div>
              <h3 className="font-semibold text-gray-900 mb-1">{t.title}</h3>
              <p className="text-sm text-gray-600">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold mb-1">Comeca a tua jornada financeira hoje!</h3>
            <p className="text-amber-100">Completa cursos, ganha certificados e melhora a tua saude financeira.</p>
          </div>
          <button onClick={() => openCourse(courses[0])}
            className="px-6 py-3 bg-white text-amber-600 font-semibold rounded-xl hover:bg-amber-50 transition-colors whitespace-nowrap">
            Comecar Agora
          </button>
        </div>
      </div>
    </div>
  );
};

export default Academia;
